/**
 * IdleLock — Layer-2 (UX) idle lock screen.
 *
 * Wraps the authenticated part of the app.  When the user has been
 * idle for `IDLE_TIMEOUT_MS` (or returns to the tab after being idle
 * for that long), the children are HIDDEN — not unmounted — and a
 * password prompt overlays the page.  Hitting `POST /api/auth/unlock`
 * with the correct password releases the gate.
 *
 * Important: children are kept mounted (`display:none`) so unsaved
 * form input, scroll position and in-flight edits survive the lock.
 *
 * State persistence:
 *   * `localStorage.last_activity_ts` — timestamp of the most recent
 *     interaction, shared across tabs.  Drives cross-tab locking.
 *   * `sessionStorage.idle_locked`    — sticky flag for THIS tab so a
 *     hard refresh cannot bypass the lock.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, LogOut } from 'lucide-react';
import { apiClient, useAuth } from '../App';

const IDLE_TIMEOUT_MS = 25 * 60 * 1000;   // 25 minutes
const THROTTLE_MS     = 30 * 1000;        // reset activity timer at most every 30s
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

const LS_LAST_ACTIVITY = 'last_activity_ts';
const SS_LOCKED_FLAG   = 'idle_locked';

const readLocked = () => {
  if (sessionStorage.getItem(SS_LOCKED_FLAG) === '1') return true;
  const last = parseInt(localStorage.getItem(LS_LAST_ACTIVITY) || '0', 10);
  return !!last && Date.now() - last >= IDLE_TIMEOUT_MS;
};
const writeLocked   = () => sessionStorage.setItem(SS_LOCKED_FLAG, '1');
const clearLocked   = () => sessionStorage.removeItem(SS_LOCKED_FLAG);
const writeActivity = (ts) => localStorage.setItem(LS_LAST_ACTIVITY, String(ts));

export default function IdleLock({ children }) {
  const { user, logout } = useAuth();

  // If nobody is logged in, IdleLock is a passthrough.
  const active = !!user;

  const [locked, setLocked]     = useState(() => (active ? readLocked() : false));
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [submitting, setSubmitting] = useState(false);

  const timerRef = useRef(null);
  const overlayRef = useRef(null);
  const inputRef = useRef(null);
  const lastRef  = useRef(Date.now());

  const lockNow = useCallback(() => {
    if (!active) return;
    writeLocked();
    setLocked(true);
  }, [active]);

  const resetTimer = useCallback(() => {
    if (!active) return;
    const now = Date.now();
    if (now - lastRef.current < THROTTLE_MS) return;
    lastRef.current = now;
    writeActivity(now);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(lockNow, IDLE_TIMEOUT_MS);
  }, [active, lockNow]);

  // If we land in a state that ALREADY qualifies as locked (hard refresh
  // after idle, opening a fresh tab after idle in another tab), lock now.
  useEffect(() => {
    if (active && readLocked()) lockNow();
  }, [active, lockNow]);

  // When user logs OUT, drop the lock so the next login starts clean.
  useEffect(() => {
    if (!active && locked) setLocked(false);
  }, [active, locked]);

  useEffect(() => {
    if (!active || locked) return;

    // If we activate into an already-idle state (cold load / fresh tab where
    // auth resolves AFTER first render, or a hard refresh after overnight
    // idle), LOCK now — do NOT seed a fresh activity stamp, which would wipe
    // the stale timestamp and silently defeat the lock.
    if (readLocked()) { lockNow(); return; }

    // seed activity timestamp on entry
    const now = Date.now();
    lastRef.current = now;
    writeActivity(now);

    // schedule using remaining time from the persisted stamp so refreshes
    // and re-mounts don't reset the clock.
    const last = parseInt(localStorage.getItem(LS_LAST_ACTIVITY) || '0', 10);
    const remaining = last ? Math.max(0, IDLE_TIMEOUT_MS - (Date.now() - last)) : IDLE_TIMEOUT_MS;
    timerRef.current = setTimeout(lockNow, remaining);

    const onActivity = () => resetTimer();
    ACTIVITY_EVENTS.forEach((e) =>
      document.addEventListener(e, onActivity, { passive: true }));

    // Cross-tab: if another tab writes a stale last_activity, lock here.
    const onStorage = (e) => {
      if (e.key === LS_LAST_ACTIVITY && e.newValue) {
        const ts = parseInt(e.newValue, 10);
        if (ts && Date.now() - ts >= IDLE_TIMEOUT_MS) lockNow();
      }
      // Another tab explicitly locked us.
      if (e.key === SS_LOCKED_FLAG && e.newValue === '1') lockNow();
    };
    window.addEventListener('storage', onStorage);

    // Coming back to a tab that was hidden/asleep: re-evaluate.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && readLocked()) lockNow();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, onActivity));
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, locked, resetTimer, lockNow]);

  // While locked: make everything outside the overlay inert (incl. portaled
  // modals/popovers) and keep focus pinned to the password field so no
  // focus-trap behind the overlay can steal it.
  useEffect(() => {
    if (!active || !locked) return;
    const root = overlayRef.current;
    const others = Array.from(document.body.children).filter((el) => el !== root && !el.hasAttribute('inert'));
    others.forEach((el) => el.setAttribute('inert', ''));
    const isInside = (n) => !!(n && root && root.contains(n));
    const guard = (e) => {
      if (isInside(e.target) || isInside(e.relatedTarget)) { e.stopImmediatePropagation(); return; }
      if (e.type === 'focusin') inputRef.current?.focus();
    };
    const GUARDED = ['focusin', 'focusout', 'pointerdown', 'mousedown', 'touchstart'];
    GUARDED.forEach((ev) => window.addEventListener(ev, guard, true));
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      GUARDED.forEach((ev) => window.removeEventListener(ev, guard, true));
      others.forEach((el) => el.removeAttribute('inert'));
    };
  }, [active, locked]);

  const onSignOut = () => {
    clearLocked();
    logout();
    window.location.href = '/login';
  };

  const onUnlock = async (e) => {
    e.preventDefault();
    if (submitting || !password.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient.post('/auth/unlock', { password }, { timeout: 15000 });
      clearLocked();
      writeActivity(Date.now());
      lastRef.current = Date.now();
      setLocked(false);
      setPassword('');
    } catch (err) {
      setError(err.isGatewayTimeout
        ? 'Server is not responding right now — please try again in a moment.'
        : (err.response?.data?.detail || 'Incorrect password. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!active) return children;

  return (
    <>
      {/* Same wrapper locked or not, so children never remount and unsaved work survives. */}
      <div aria-hidden={locked || undefined} style={{ display: locked ? 'none' : 'contents' }}>{children}</div>

      {locked && createPortal(
      <div data-testid="idle-lock-overlay" ref={overlayRef}
           style={{ zIndex: 2147483647, pointerEvents: 'auto' }}
           className="fixed inset-0 flex items-center justify-center bg-slate-950/95 backdrop-blur-sm">
        <form onSubmit={onUnlock}
              className="w-full max-w-sm mx-4 p-6 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl space-y-4">
          <div className="flex items-center gap-2 text-slate-100">
            <Lock className="h-4 w-4 text-fuchsia-400" />
            <h2 className="text-sm font-semibold tracking-tight">Session locked</h2>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            You&apos;ve been inactive for a while.  Enter your password to resume — your work is preserved.
          </p>
          {user?.username || user?.email ? (
            <div className="text-[11px] font-mono text-slate-500">
              {user.username || user.email}
            </div>
          ) : null}
          <input type="password"
                 ref={inputRef}
                 autoFocus
                 autoComplete="current-password"
                 value={password}
                 disabled={submitting}
                 data-testid="idle-lock-password"
                 onChange={(e) => { setPassword(e.target.value); setError(''); }}
                 placeholder="Password"
                 className="w-full px-3 py-2.5 rounded-lg bg-slate-950 border border-slate-800 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40" />
          {error ? (
            <p data-testid="idle-lock-error" className="text-xs text-rose-400">{error}</p>
          ) : null}
          <button type="submit"
                  disabled={submitting || !password.trim()}
                  data-testid="idle-lock-submit"
                  className="w-full py-2.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors">
            {submitting ? 'Unlocking…' : 'Unlock'}
          </button>
          <button type="button" onClick={onSignOut} data-testid="idle-lock-signout"
                  className="w-full py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors">
            <LogOut className="h-3.5 w-3.5" /> Not you? Sign out
          </button>
          <p className="text-[10px] text-slate-500 text-center">
            Your work is preserved — this only re-verifies your identity. Signing out discards unsaved work.
          </p>
        </form>
      </div>, document.body)}
    </>
  );
}
